import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounceValue } from "usehooks-ts";
import { backButton, hapticFeedback } from "@telegram-apps/sdk-react";
import { useGetProfile } from "@/pages/profile/api";
import {
  useSearchUsers,
  useP2PTransfer,
  useP2PQuote,
  P2PUser,
  P2PTransferResponse,
  P2PKeys,
} from "./api";
import RecentRecipients from "./components/RecentRecipients.tsx";
import SenderBalanceCard from "./components/SenderBalanceCard.tsx";
import RecipientCard from "./components/RecipientCard.tsx";
import { Input } from "@/components/ui/input.tsx";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { formatNumber } from "@/shared/utils";
import IconSearch from "@/assets/icons/search.svg?react";
import UserIcon from "@/assets/icons/user-circle.svg?react";
import DonLinear from "@/assets/icons/don-linear.svg?react";
import IconInfoCircle from "@/assets/icons/info-circle-linear.svg?react";
import { Phone as PhoneIcon, ChevronRight } from "lucide-react";
import P2pFAQSheet from "./components/P2pFAQSheet.tsx";
import TransferReceipt from "./components/TransferReceipt.tsx";
import { useSharePhone } from "@/pages/profile/api/useSharePhone.ts";
import { useVirtualKeyboard } from "@/shared/hooks/useAnimatedVirtualKeyboard.ts";
import { useTranslation } from "react-i18next";
import ParticipantCard from "@/pages/challenges/components/ParticipantCard.tsx";
import UserSearchSkeleton from "./components/UserSearchSkeleton.tsx";
import { ImageType } from "@/pages/challenges/types/common";
import {
  formatCardLabel,
  BankCard,
  useMyCards,
} from "@/pages/profile/cards/api.ts";
import { useCardsStore } from "@/pages/profile/cards/store.ts";
import AddCardSheet from "@/pages/profile/cards/AddCardSheet.tsx";
import CardSwitcherSheet from "@/pages/profile/cards/components/CardSwitcherSheet.tsx";
import { AnimatePresence, motion } from "framer-motion";

/* ---- Avatar adapter ---- */

function avatarFromUrl(url: string | null): ImageType | null {
  if (!url) return null;
  return {
    id: "",
    url,
    tiny_url: url,
    small_url: url,
    medium_url: url,
    large_url: url,
    name: "",
    size: 0,
    video: null,
  };
}

/* ---- Main Page ---- */

type Step = "search" | "review" | "success";
const QUICK_AMOUNTS = [5000, 10000, 50000, 100000];

function getP2PQuoteMessage(
  quote: { error_code: string | null; error_message: string | null; min_amount: string; max_amount: string | null },
  fallback: string
): string {
  if (quote.error_code === "below_minimum") {
    return `Minimal miqdor: ${formatNumber(quote.min_amount)} DON`;
  }
  if (quote.error_code === "above_maximum" && quote.max_amount) {
    return `Maksimal miqdor: ${formatNumber(quote.max_amount)} DON`;
  }
  return quote.error_message || fallback;
}

export default function P2PPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const hasPhone = !!profile?.phone;
  const inputRef = useRef<HTMLInputElement>(null);
  const { isKeyboardOpen } = useVirtualKeyboard();

  // Keyboard-aware scroll reset. Lighter than full body-lock — iOS still
  // tries to auto-scroll the focused input on screen but we just yank
  // window scroll back to 0 a couple of times so the fixed root visibly
  // stays anchored. Body locking interfered with bottom sheets opening.
  useEffect(() => {
    if (!isKeyboardOpen) return;
    const reset = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }
    };
    reset();
    const ids = [50, 150, 300, 500, 800, 1200].map((d) => setTimeout(reset, d));
    const onScroll = () => reset();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.visualViewport?.addEventListener("scroll", onScroll);
    window.visualViewport?.addEventListener("resize", onScroll);
    return () => {
      ids.forEach(clearTimeout);
      window.removeEventListener("scroll", onScroll);
      window.visualViewport?.removeEventListener("scroll", onScroll);
      window.visualViewport?.removeEventListener("resize", onScroll);
    };
  }, [isKeyboardOpen]);

  // Lock body scroll while on P2P page (CSS only — touchmove block
  // interfered with iOS keyboard auto-scroll inside the AddCardSheet).
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  const { t } = useTranslation();

  const [step, setStep] = useState<Step>("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery] = useDebounceValue(searchQuery, 300);
  const [selectedUser, setSelectedUser] = useState<P2PUser | null>(null);
  const [amount, setAmount] = useState("");
  const [cardSheetOpen, setCardSheetOpen] = useState(false);
  const [switcherSheetOpen, setSwitcherSheetOpen] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  // Captured at transfer-success time. Holds backend's transfer_id +
  // commission_uzs which the receipt needs but the form state doesn't carry.
  // completedAt is set client-side because the backend response doesn't
  // include a timestamp and we render the receipt immediately on success.
  const [transferResult, setTransferResult] =
    useState<{ result: P2PTransferResponse; completedAt: Date } | null>(null);
  // Idempotency key for the CURRENT transfer intent. Stable across retries of
  // the same (receiver, amount, card) so the backend rejects a replay and
  // never charges twice; regenerated when the intent changes or after a
  // completed transfer.
  const idemRef = useRef<{ sig: string; key: string } | null>(null);
  const quoteToastRef = useRef<string | null>(null);
  const quoteToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: searchResults, isLoading: isSearching } =
    useSearchUsers(debouncedQuery);
  const transferMutation = useP2PTransfer();
  const { data: cards = [] } = useMyCards();

  // Default karta: birinchi default=true, yo'q bo'lsa birinchi aktiv,
  // yo'q bo'lsa null. Tanlovni eslab qoladigan store keyinchalik
  // ko'p kartali UI uchun ulanadi.
  const selectedCardId = useCardsStore((s) => s.selectedCardId);
  const setSelectedCardId = useCardsStore((s) => s.setSelectedCardId);
  const activeCard: BankCard | null = useMemo(() => {
    if (cards.length === 0) return null;
    if (selectedCardId) {
      const match = cards.find((c) => c.id === selectedCardId);
      if (match) return match;
    }
    return cards.find((c) => c.is_default) || cards[0];
  }, [cards, selectedCardId]);

  const walletAmount = parseFloat(profile?.wallet_amount || "0");
  const numAmount = parseFloat(amount || "0");
  const isOverBalance = numAmount > walletAmount;

  // Komissiyani server'dan olamiz (P2PSettings) — admin'dan
  // o'zgartiriladigan qiymatlar (rate, percent, min, max).
  const { data: quote } = useP2PQuote(numAmount, numAmount > 0 && !isOverBalance);
  const commissionUzs = quote ? parseFloat(quote.commission_uzs) : 0;
  const commissionPercent = quote ? parseFloat(quote.commission_percent) : 0;

  useEffect(() => {
    if (quoteToastTimerRef.current) {
      clearTimeout(quoteToastTimerRef.current);
      quoteToastTimerRef.current = null;
    }
    if (!quote || quote.valid || !amount) {
      quoteToastRef.current = null;
      return;
    }
    if (!["below_minimum", "above_maximum", "invalid_amount"].includes(quote.error_code || "")) {
      return;
    }
    const signature = `${quote.error_code}:${amount}:${quote.error_message}`;
    if (quoteToastRef.current === signature) return;

    quoteToastTimerRef.current = setTimeout(() => {
      quoteToastRef.current = signature;
      showCustomToast(
        getP2PQuoteMessage(quote, t("p2p_error") || "Miqdor noto'g'ri"),
        "warning"
      );
    }, 2500);

    return () => {
      if (quoteToastTimerRef.current) {
        clearTimeout(quoteToastTimerRef.current);
        quoteToastTimerRef.current = null;
      }
    };
  }, [amount, quote, t]);

  // Step-aware Telegram back button — own the wiring (no PageWrapper)
  // because PageWrapper unconditionally navigates to backPath, which
  // races with the in-page step transition and exits the page entirely.
  useEffect(() => {
    if (!backButton.isMounted()) return;
    backButton.show();
    return backButton.onClick(() => {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred("soft");
      }
      if (step === "review") setStep("search");
      else navigate("/");
    });
  }, [step, navigate]);

  // Phone is a prerequisite for P2P (backend requires it on /SetPhone +
  // card-binding paths). Re-fetch profile after the share so the inline
  // gate disappears without a manual reload.
  const { sharePhone, isPending: isSharingPhone } = useSharePhone({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const handleSelectUser = (user: P2PUser) => {
    setSelectedUser(user);
    setAmount("");
    setStep("review");
  };

  const handleAmountChange = (val: string) => {
    setAmount(val.replace(/\D/g, ""));
  };

  const handleSendTransfer = async (cardOverride?: BankCard) => {
    if (!numAmount || numAmount <= 0) {
      showCustomToast(t("p2p_enter_amount_warning"), "warning");
      return;
    }
    if (isOverBalance) return;
    if (quote && !quote.valid) {
      showCustomToast(
        getP2PQuoteMessage(quote, t("p2p_error") || "Xatolik"),
        "error"
      );
      return;
    }

    const card = cardOverride || activeCard;
    if (!card) {
      setCardSheetOpen(true);
      return;
    }
    if (!selectedUser?.username) return;

    // Stable idempotency key for this exact (receiver, amount, card) intent.
    // Reused on retries so an accidental replay can't double-charge; a changed
    // intent gets a fresh key.
    const sig = `${selectedUser.username}:${amount}:${card.id}`;
    if (!idemRef.current || idemRef.current.sig !== sig) {
      const key =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      idemRef.current = { sig, key };
    }

    try {
      const result = await transferMutation.mutateAsync({
        receiver_username: selectedUser.username,
        amount: parseFloat(amount),
        card_id: card.id,
        idempotency_key: idemRef.current.key,
      });

      if (result.success) {
        // No confetti/sound on the P2P receipt — sending money is a
        // financial action, not an achievement.
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: [P2PKeys.Recent] });

        const status = (result.status || "").toLowerCase();
        if (status === "completed") {
          // Fully settled — clear the key so a later identical transfer isn't
          // wrongly deduped, then show the receipt. transfer_id/commission_uzs
          // live only on the response, so snapshot them for the receipt.
          idemRef.current = null;
          setTransferResult({ result, completedAt: new Date() });
          setStep("success");
        } else {
          // Charge still settling (PENDING_PAYMENT/PAID) — the receiver is NOT
          // credited yet, so we must NOT show a "sent successfully" receipt.
          // Keep the key (a retry stays deduped) and send the user home to
          // watch history. The reconciler finishes or refunds it.
          showCustomToast(
            result.message ||
              t("p2p_processing") ||
              "To'lov tasdiqlanmoqda — o'tkazma tez orada yakunlanadi",
            "warning"
          );
          navigate("/");
        }
      }
    } catch (err: any) {
      // Backend tags each error with a code (`detail.error`); map known codes
      // to specific UX. Falls back to the server's message, then a generic
      // toast. `phone_required` shouldn't fire from the transfer endpoint
      // (we gate at page entry) but we surface it for safety.
      const code = err?.response?.data?.detail?.error;
      const serverMessage = err?.response?.data?.detail?.message;
      let message: string;
      switch (code) {
        case "feature_disabled":
          message = t("p2p_feature_disabled") || "P2P o'tkazma vaqtincha o'chirilgan";
          break;
        case "phone_required":
          message = t("p2p_phone_required") || "Telefon raqamingizni ulang";
          break;
        case "card_not_found":
          message = t("p2p_card_not_found") || "Karta topilmadi, qaytadan ulang";
          break;
        case "user_not_found":
          message = t("p2p_user_not_found") || "Foydalanuvchi topilmadi";
          break;
        case "self_transfer":
          message = t("p2p_self_transfer") || "O'zingizga DON yuborib bo'lmaydi";
          break;
        case "wallet_not_found":
        case "receiver_wallet_not_found":
          message = t("p2p_wallet_not_found") || "Hamyon topilmadi";
          break;
        case "not_enough_don_balance":
          // Sender's DON balance (not the card) — distinct from
          // insufficient_funds so the message is accurate/localized.
          message =
            t("p2p_not_enough_balance") ||
            "Hisobingizda yetarli DON mavjud emas";
          break;
        case "duplicate_request":
          // Idempotency replay (double-tap / retry). The original is already
          // in flight or done — don't imply it failed.
          message =
            t("p2p_duplicate_request") ||
            "Bu o'tkazma allaqachon bajarilmoqda";
          break;
        case "invalid_amount":
        case "below_minimum":
        case "above_maximum":
          message = quote
            ? getP2PQuoteMessage(quote, t("p2p_error") || "Miqdor noto'g'ri")
            : serverMessage || t("p2p_error") || "Miqdor noto'g'ri";
          break;
        case "phone_in_use":
          message = t("p2p_phone_in_use") || "Bu telefon raqami boshqa foydalanuvchida";
          break;
        case "network_error":
          // User-side connectivity issue — kept distinct from a gateway
          // outage so the advice ("check your connection") is actionable.
          message =
            t("p2p_network_error") ||
            "Tarmoq xatosi. Internet aloqangizni tekshirib qayta urinib ko'ring";
          break;
        case "server_error":
        case "auth_error":
          // Transient gateway/auth failure — retrying later can succeed.
          message =
            t("p2p_service_unavailable") ||
            "Xizmat vaqtincha ishlamayapti. Birozdan so'ng qayta urinib ko'ring";
          break;
        case "card_commission_insufficient_funds":
        case "insufficient_funds":
          // Domopay card charge failed for the UZS commission, not the DON
          // wallet balance. Keep this literal so stale/admin translations
          // cannot show the misleading "DON yetarli emas" copy here.
          message = "Kartadan komissiya to'lovi o'tmadi. Karta mablag'i yoki limitini tekshiring";
          break;
        case "payment_failed":
          // Generic charge failure — point the user at the card/balance
          // rather than implying it's a temporary outage.
          message =
            t("p2p_payment_failed") ||
            "To'lov amalga oshmadi. Karta yoki mablag'ingizni tekshiring";
          break;
        default:
          message = serverMessage || t("p2p_error") || "Xatolik";
      }
      showCustomToast(message, "error");
    }
  };

  const handleReset = () => {
    setStep("search");
    setSearchQuery("");
    setSelectedUser(null);
    setAmount("");
    setTransferResult(null);
    idemRef.current = null; // fresh intent -> fresh idempotency key
  };

  // Top-pinned + h-dvh — `bottom: 0` ishlatmaymiz chunki klaviatura
  // chiqqanda iOS layout viewport'ni siqmaydi (faqat dvh shrink bo'ladi).
  // Shu bilan SendButton va summary card keyboard ostiga tushib ketmaydi.
  return (
    <>
    <div
      className="container pb-4 flex flex-col overflow-hidden"
      style={{
        height:
          "calc(var(--tg-viewport-height, 100svh) - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
      }}
    >
      {/* Title + FAQ row — faqat search step'da ko'rsatamiz. Review
          step'da Sender/Recipient card va Amount block uchun vertikal
          joy zarur (klaviatura ochilganda quick-button suggestionlar
          komissiya portali ostiga tushib ketmasligi uchun). Success
          step'da chek o'z header'iga ega, FAQ ham mantiqsiz. */}
      {step === "search" && (
        <div className="flex items-center justify-between gap-3 pt-3 mb-3">
          <h1 className="text-lg font-semibold text-white leading-130">
            {t("p2p_title") || "P2P o'tkazma"}
          </h1>
          <VibratingButton
            onClick={() => setIsFAQOpen(true)}
            vibrationType="selection"
            aria-label={t("p2p_faq_title") || "FAQ"}
            className="flex items-center gap-1.5 px-3 py-1.5 -mr-1 rounded-full bg-white/[0.04] border border-white/[0.08] active:bg-white/[0.08] transition-colors"
          >
            <IconInfoCircle className="size-4 text-orange-100" />
            <span className="text-sm font-semibold text-white leading-none">
              FAQ
            </span>
          </VibratingButton>
        </div>
      )}

      {!isProfileLoading && !hasPhone && (
        <PhoneRequiredStep
          onShare={sharePhone}
          isSharing={isSharingPhone}
        />
      )}

      {hasPhone && step === "search" && (
        <SearchStep
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults || []}
          isSearching={isSearching && debouncedQuery.length >= 1}
          onSelect={handleSelectUser}
        />
      )}

      {hasPhone && step === "review" && profile && selectedUser && (
        <ReviewStep
          senderAvatar={profile.avatar}
          senderUsername={profile.username}
          senderIsPremium={profile.is_premium}
          walletAmount={walletAmount}
          recipient={selectedUser}
          amount={amount}
          commissionUzs={commissionUzs}
          commissionPercent={commissionPercent}
          activeCard={activeCard}
          canSwitchCard={cards.length > 1}
          onSwitchCard={() => setSwitcherSheetOpen(true)}
          onAmountChange={handleAmountChange}
          onSend={() => handleSendTransfer()}
          isOverBalance={isOverBalance}
          isSending={transferMutation.isPending}
          inputRef={inputRef}
          quoteError={
            quote && !quote.valid
              ? getP2PQuoteMessage(quote, t("p2p_error") || "Xatolik")
              : null
          }
          quoteErrorCode={quote?.error_code || null}
        />
      )}

      {hasPhone && step === "success" && selectedUser && transferResult && profile && (
        <TransferReceipt
          amount={amount}
          commissionUzs={transferResult.result.commission_uzs || "0"}
          transferId={transferResult.result.transfer_id}
          completedAt={transferResult.completedAt}
          sender={{
            avatar: profile.avatar,
            fullName: profile.full_name,
            username: profile.username,
            isPremium: profile.is_premium,
          }}
          receiver={selectedUser}
          onDone={() => navigate("/")}
          onSendMore={handleReset}
        />
      )}

</div>

      {/* AddCardSheet fixed parent'dan tashqarida render bo'ladi — aks
          holda parent'ning overflow-hidden Sheet ichidagi absolute
          elementlarni kesib qo'yib, sheet ko'rinmay qolardi. */}
      <AddCardSheet
        isOpen={cardSheetOpen}
        onClose={() => setCardSheetOpen(false)}
        onAdded={(card) => {
          setSelectedCardId(card.id);
          // Sheet yopilgach yangi karta to'g'ridan-to'g'ri uzatiladi —
          // useMyCards cache hali invalidate bo'lmasligi mumkin, shuning
          // uchun closure'dagi `activeCard` eski qiymatga qarab transfer
          // sheet'ni yana ochib qo'ymasligi uchun cardOverride yuboramiz.
          setCardSheetOpen(false);
          handleSendTransfer(card);
        }}
      />

      <CardSwitcherSheet
        isOpen={switcherSheetOpen}
        onClose={() => setSwitcherSheetOpen(false)}
      />

      <P2pFAQSheet
        isOpen={isFAQOpen}
        onClose={() => setIsFAQOpen(false)}
      />
    </>
  );
}

/* ---- Search Step ---- */

function SearchStep({
  searchQuery,
  setSearchQuery,
  searchResults,
  isSearching,
  onSelect,
}: {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  searchResults: P2PUser[];
  isSearching: boolean;
  onSelect: (user: P2PUser) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="relative mb-4">
        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-1" />
        <Input
          className="pl-10"
          placeholder={t("p2p_search_placeholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
      </div>

      {searchQuery.length === 0 && <RecentRecipients onSelect={onSelect} />}

      <div className="flex-1 flex flex-col gap-3 overflow-y-auto min-h-0">
        {isSearching &&
          Array.from({ length: 3 }).map((_, i) => <UserSearchSkeleton key={i} />)}

        {!isSearching &&
          searchQuery.length >= 1 &&
          searchResults.length === 0 && (
            <div className="flex-center flex-col py-10">
              <UserIcon className="size-12 text-gray-1 mb-3" />
              <p className="text-sm text-gray-1 text-center">
                {t("p2p_user_not_found")}
              </p>
            </div>
          )}

        {!isSearching &&
          searchResults.map((user) => (
            <VibratingButton
              key={user.id}
              onClick={() => onSelect(user)}
              className="w-full text-start"
            >
              <ParticipantCard
                avatar={avatarFromUrl(user.avatar_url)}
                fullName={user.full_name || ""}
                username={user.username || ""}
                isPremium={false}
                isReferral
              />
            </VibratingButton>
          ))}

        {!searchQuery && (
          <div className="flex-center flex-col py-10">
            <IconSearch className="size-12 text-gray-1 mb-3 opacity-30" />
            <p className="text-sm text-gray-1 text-center">
              {t("p2p_search_hint")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- Review Step (sender + amount + recipient + send) ---- */

function ReviewStep({
  senderAvatar,
  senderUsername,
  senderIsPremium,
  walletAmount,
  recipient,
  amount,
  commissionUzs,
  commissionPercent,
  activeCard,
  canSwitchCard,
  onSwitchCard,
  onAmountChange,
  onSend,
  isOverBalance,
  isSending,
  inputRef,
  quoteError,
  quoteErrorCode,
}: {
  senderAvatar: ImageType | null;
  senderUsername: string | null;
  senderIsPremium: boolean;
  walletAmount: number;
  recipient: P2PUser;
  amount: string;
  commissionUzs: number;
  commissionPercent: number;
  activeCard: BankCard | null;
  canSwitchCard: boolean;
  onSwitchCard: () => void;
  onAmountChange: (v: string) => void;
  onSend: () => void;
  isOverBalance: boolean;
  isSending: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  quoteError: string | null;
  quoteErrorCode: string | null;
}) {
  const hasCard = activeCard !== null;
  const { t } = useTranslation();
  const numAmount = parseFloat(amount || "0");
  const amountValidationMessage =
    numAmount > walletAmount
      ? t("p2p_not_enough_don")
      : quoteError && quoteErrorCode !== "feature_disabled"
      ? quoteError
      : null;

  // Bottom portal (komissiya card + Send button) ning real balandligini
  // o'lchaymiz — content ostida shu portal egallaydigan joyni paddingBottom
  // sifatida qo'shish uchun. Aks holda komissiya card amount block ustiga
  // tushib, ko'rinmay qoladi. ResizeObserver komissiya foiz/karta o'zgarishi
  // bilan height jumping bo'lganda ham yangilab boradi.
  const bottomPortalRef = useRef<HTMLDivElement>(null);
  const [bottomPortalHeight, setBottomPortalHeight] = useState(140);
  useEffect(() => {
    const el = bottomPortalRef.current;
    if (!el) return;
    const update = () => setBottomPortalHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cheklov: balansdan ko'proq raqam kiritilmaydi (lekin yangi user
  // uchun kamida 6 raqamga ruxsat — tezkor qiymatlar bosish uchun).
  const maxAmountDigits = Math.max(
    String(Math.floor(walletAmount)).length,
    6
  );

  // Uzun sonlarda font kichrayadi va horizontal scroll chiqmaydi.
  // Display value with thousand separators ("1 000 000"), internal value
  // stays as raw digits ("1000000").
  const displayAmount = amount
    ? Number(amount).toLocaleString("ru-RU").replace(/\u00a0/g, " ")
    : "";

  // text-4xl = 36px, text-3xl = 30px, text-2xl = 24px (Tailwind defaults).
  // charPx — input width hisoblash uchun har raqamning taxminiy pikseli.
  const amountFontClass = (() => {
    const len = displayAmount.length || 1;
    if (len <= 7) return { text: "text-4xl", icon: "size-9", charPx: 26 };
    if (len <= 11) return { text: "text-3xl", icon: "size-8", charPx: 22 };
    return { text: "text-2xl", icon: "size-7", charPx: 18 };
  })();

  // Auto-focus the amount field once the cards mount.
  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(id);
  }, [inputRef]);

  // scrollIntoView olib tashlandi — interactive-widget=resizes-content
  // viewport meta tag bilan iOS keyboard layoutni avto siqadi va native
  // browser focused inputni ko'rinib turishini ta'minlaydi. Manual scroll
  // swipe paytida caret-ni o'ynatardi.

  return (
    <div
      className="flex-1 flex flex-col pt-2 transition-[padding]"
      style={{
        // index.html'da interactive-widget=resizes-content bor — klaviatura
        // ochilganda layout viewport o'zi qisqaradi, shuning uchun JS
        // keyboardHeight QO'SHMAYMIZ (Pro Max'da u double-count bo'lib
        // tugmani ortiqcha ko'tarib, ostida bo'sh joy qoldirar edi). Faqat
        // sticky bottom portal balandligi + portal o'zi turadigan offset
        // (keyboard-inset/safe-area) qadar joy qoldiramiz.
        paddingBottom: `calc(${bottomPortalHeight}px + max(env(keyboard-inset-height, 0px), env(safe-area-inset-bottom, 0px), 16px) + 8px)`,
      }}
    >
      {/*style={{ paddingTop: "16px" }}*/}
      <div
        className="flex-1 flex flex-col items-stretch min-h-0 overflow-y-auto"
      >
        {/* Sender → recipient cards. Cards orasida kichik gap, chevron ikon
            absolute markazda, bg-dark + ring orqali "kesib o'tilgan" illyuziya. */}
        <div className="relative flex flex-col gap-2">
          <SenderBalanceCard
            avatar={senderAvatar}
            username={senderUsername}
            walletAmount={walletAmount}
            isPremium={senderIsPremium}
          />

          <RecipientCard
            fullName={recipient.full_name || ""}
            username={recipient.username}
            avatarUrl={recipient.avatar_url}
          />

          {/* Chevron connector — markazda, ikkita card o'rtasida turadi.
              Cards orasidagi gap (8px) + ring (4px) bilan smooth ko'rinadi. */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="size-9 rounded-full bg-gray-4/90  border border-orange-100/30 flex-center ring-4 ring-[#0e0e14]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className="text-orange-100"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14" />
                <path d="m19 12-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Amount block — cards ostida, miqdor kiritish uchun */}
        <div className="flex-center flex-col gap-3 py-6">
          <p className="text-xs text-gray-1 leading-130">
            {t("p2p_enter_amount")}
          </p>
          {/* Input + DON ikon. Oddiy controlled input — fixed kenglik,
              center text. iOS Telegram WebView'da hidden span pattern
              event'larni unstable qilardi (backspace ham yozuvni o'zgartirmasdi). */}
          {/* Icon va input qator bo'lib center'da. Input fixed kenglikka
              ega (maxAmountDigits asosida), shuning uchun raqam kiritilganda
              re-flow bo'lmaydi. Icon shrink-0 — hech qachon kichraymaydi
              va matn bilan yonma-yon turadi. */}
          <div className="flex items-center justify-center gap-2 max-w-full px-4">
            <DonLinear className="shrink-0 text-orange-100 size-9" />
            <div className={`flex items-center font-bold ${amountFontClass.text}`}>
              {!displayAmount && (
                <span className="text-white/20" aria-hidden>
                  0
                </span>
              )}
              <input
                ref={inputRef}
                className="caret-orange-100 border-none bg-transparent text-white outline-none p-0 selection:bg-transparent selection:text-white"
                placeholder=""
                type="tel"
                inputMode="numeric"
                min={0}
                value={displayAmount}
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => {
                  const raw = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, maxAmountDigits);
                  onAmountChange(raw);
                }}
                onInput={(e) => {
                  // iOS Telegram WebView ba'zan onChange'ni chiqarmaydi
                  // (autocorrect/autosuggest) — onInput defensive backup.
                  const target = e.currentTarget;
                  const raw = target.value
                    .replace(/\D/g, "")
                    .slice(0, maxAmountDigits);
                  if (raw !== amount) onAmountChange(raw);
                }}
                style={{
                  width: displayAmount
                    ? `${displayAmount.length * amountFontClass.charPx + 4}px`
                    : `2px`,
                  paddingLeft: displayAmount ? "2px" : "0",
                  maxWidth: "min(100%, 320px)",
                  transform: "translateZ(0)",
                  WebkitTransform: "translateZ(0)",
                  WebkitTapHighlightColor: "transparent",
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 w-full max-w-[328px] px-1">
            {QUICK_AMOUNTS.map((val) => (
              <VibratingButton
                key={val}
                vibrationType="selection"
                onClick={() => {
                  if (val <= walletAmount) onAmountChange(String(val));
                  inputRef.current?.focus();
                }}
                className={`h-9 rounded-full bg-white/[0.06] flex-center text-sm leading-130 transition-200 whitespace-nowrap ${
                  val > walletAmount ? "opacity-40" : ""
                } ${
                  amount === String(val)
                    ? "!bg-orange-100/20 text-orange-100"
                    : "text-white"
                }`}
                disabled={val > walletAmount}
              >
                {formatNumber(val)}
              </VibratingButton>
            ))}
          </div>

          {/* Amount validation — invisible placeholder layoutni saqlaydi,
              ustida AnimatePresence orqali smooth fade+slide bilan real
              matn chiqadi/yo'qoladi. iOS Telegram WebView'da opacity
              transition kechikishidan farqli, framer-motion DOM'dan
              tozalab tushiradi va matn ishonchli yo'qoladi. */}
          <div className="relative">
            <p
              className="text-xs leading-130 invisible"
              aria-hidden
            >
              {amountValidationMessage || t("p2p_not_enough_don")}
            </p>
            <AnimatePresence>
              {amountValidationMessage && (
                <motion.p
                  key={amountValidationMessage}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="absolute inset-0 text-xs leading-130 text-red text-center"
                >
                  {amountValidationMessage}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>

      {createPortal(
        <div
          ref={bottomPortalRef}
          className="fixed container right-0 left-0 bg-[linear-gradient(180deg,#0E0E1400_0%,_#0E0E14_30%)] z-10 transition-[bottom] duration-150 pt-4"
          style={{
            // interactive-widget=resizes-content (index.html) shrinks the
            // layout viewport when the keyboard opens, so a fixed element
            // pinned to the bottom already sits right above the keypad —
            // no JS keyboardHeight offset (that double-counted on Pro Max
            // and floated the button up with a gap). env(keyboard-inset-
            // height) stays as a defensive fallback for clients that don't
            // resize; safe-area/16px covers the keyboard-closed state.
            bottom:
              "max(env(keyboard-inset-height, 0px), env(safe-area-inset-bottom, 0px), 16px)",
            willChange: "transform",
            transform: "translate3d(0, 0, 0)",
          }}
        >
        {/* Komissiya + karta card — Send button ustida sticky. Karta tanlovi
            amount=0 bo'lganda ham ko'rinadi; aks holda user qaysi kartadan
            komissiya yechilishini ko'rmay qoladi. */}
        <div
          className="rounded-xl bg-gray-4 border border-white/[0.04] p-3 flex flex-col gap-2 mb-3"
        >
          <div
            className={`flex items-center justify-between transition-opacity duration-200 ${
              numAmount > 0 ? "opacity-100" : "opacity-45"
            }`}
          >
            <span className="text-xs text-gray-1 leading-130">
              {t("p2p_commission") || "Komissiya"}
              {commissionPercent > 0
                ? ` (${commissionPercent}%, ${t("p2p_from_card") || "kartadan"})`
                : ""}
            </span>
            <span className="text-sm font-bold text-orange-100">
              {formatNumber(commissionUzs)} UZS
            </span>
          </div>
          {activeCard && (
            canSwitchCard ? (
              <button
                type="button"
                onClick={onSwitchCard}
                className="flex items-center justify-between border-t border-white/[0.04] pt-2"
                aria-label={t("p2p_select_card") || "Karta tanlang"}
              >
                <span className="text-xs text-gray-1 leading-130">
                  {t("p2p_card") || "Karta"}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-white">
                  {formatCardLabel(activeCard)}
                  <ChevronRight className="size-3.5 text-gray-1" strokeWidth={2.5} />
                </span>
              </button>
            ) : (
              <div className="flex items-center justify-between border-t border-white/[0.04] pt-2">
                <span className="text-xs text-gray-1 leading-130">
                  {t("p2p_card") || "Karta"}
                </span>
                <span className="text-xs font-medium text-white">
                  {formatCardLabel(activeCard)}
                </span>
              </div>
            )
          )}
        </div>

        <BaseButton
          className="w-full disabled:!bg-white/08 disabled:!text-gray-1"
          variant="premium"
          text={
            !hasCard
              ? t("p2p_connect_card") || "Karta ulash"
              : t("p2p_send")
          }
          onClick={() => onSend()}
          disabled={
            !amount ||
            numAmount <= 0 ||
            isOverBalance ||
            Boolean(quoteError && quoteErrorCode !== "feature_disabled")
          }
          isLoading={isSending}
          vibrate
          vibrationType="soft"
        />
        </div>,
        document.body
      )}
    </div>
  );
}

/* ---- Phone Required Step ----
   Inline empty-state shown when the user lands on P2P without a saved
   phone number. P2P transfers, card binding, and Domopay OTP all require
   a phone — surfacing the gate up-front avoids a confusing "feature_disabled"
   toast deep in the flow. The share itself is a single Telegram tap
   (`requestContactComplete`), so we don't wrap it in step counters. */

function PhoneRequiredStep({
  onShare,
  isSharing,
}: {
  onShare: () => void;
  isSharing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex-1 min-h-0 flex flex-col items-stretch justify-center px-2 pb-[calc(132px+env(safe-area-inset-bottom))]">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="flex flex-col items-center text-center gap-4 max-w-sm mx-auto"
      >
        <div className="size-16 rounded-full bg-orange-100/12 flex-center">
          <PhoneIcon className="size-7 text-orange-100" strokeWidth={2} />
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-white leading-130">
            {t("p2p_phone_required_title") ||
              "Telefon raqamingizni ulang"}
          </h2>
          <p className="text-sm text-gray-1 leading-140">
            {t("p2p_phone_required_desc") ||
              "P2P o'tkazma va karta ulash uchun telefon raqamingiz kerak. Telegram orqali bir tugma bilan ulashasiz."}
          </p>
        </div>
      </motion.div>

      {createPortal(
        <div
          className="fixed container right-0 left-0 z-10"
          style={{ bottom: "max(env(safe-area-inset-bottom), 16px)" }}
        >
          <BaseButton
            className="w-full"
            variant="premium"
            text={
              t("p2p_share_phone_cta") || "Telegram orqali ulash"
            }
            onClick={onShare}
            isLoading={isSharing}
            vibrate
            vibrationType="soft"
          />
        </div>,
        document.body
      )}
    </div>
  );
}
